import React, {useState, useEffect} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrAccordion from '../../../../common/arr-accordion/ArrAccordion';
import ArrTransferList, {
  KeyValue,
} from '../../../../common/arr-transfer-list/ArrTransferList';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import './MetadataFields.scss';
import {ReactComponent as ArrowBackward} from '../../../../../assets/images/back-arrow.svg';
import {ReactComponent as ArrowForward} from '../../../../../assets/images/arrow-forward.svg';
import {ReactComponent as DoubleArrowBackward} from '../../../../../assets/images/double-arrow-backward.svg';
import {ReactComponent as DoubleArrowForward} from '../../../../../assets/images/double-arrow-forward.svg';
import {ReactComponent as RowChecked} from '../../../../../assets/images/row-checked.svg';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  setFields,
  IRequestField,
} from '../../../../../redux/slice/createCustomIndexSlice';

import {METADATA_FIELD_OPTIONS} from './metadataFieldOptions';

const DEFAULT_ANALYZER_ID = 1;

const MetadataFields: React.FC = () => {
  const dispatch = useDispatch();
  const savedFields: IRequestField[] =
    useSelector(
      (state: RootState) => state.indexManagement.createCustomIndex.Fields
    ) ?? [];

  const mockMetadataFields: KeyValue[] = METADATA_FIELD_OPTIONS;

  const isReadOnlyView = useSelector(
    (state: RootState) => state.indexManagement.isReadOnlyView
  );
  const savedSelected: KeyValue[] = mockMetadataFields.filter((field) =>
    savedFields.some((saved) => saved.FieldId === field.id)
  );
  const savedAvailable: KeyValue[] = mockMetadataFields.filter(
    (field) => !savedFields.some((saved) => saved.FieldId === field.id)
  );

  const [rightList, setRightList] = useState<KeyValue[]>(savedSelected);
  const [leftList, setLeftList] = useState<KeyValue[]>(savedAvailable);
  const [leftSelected, setLeftSelected] = useState<KeyValue[]>([]);
  const [rightSelected, setRightSelected] = useState<KeyValue[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');

  useEffect(() => {
    setRightList(savedSelected);
    setLeftList(savedAvailable);
  }, [savedSelected, savedAvailable]);

  const filteredLeftList = leftList.filter((i) =>
    i.label.toLowerCase().includes(leftSearch.toLowerCase())
  );

  const filteredRightList = rightList.filter((i) =>
    i.label.toLowerCase().includes(rightSearch.toLowerCase())
  );

  const syncToStore = (items: KeyValue[]) => {
    const updatedFields: IRequestField[] = items.map((item) => {
      const existingField = savedFields.find(
        (saved) => saved.FieldId === Number(item.id)
      );

      return {
        FieldId: Number(item.id),
        AnalyzerId: existingField?.AnalyzerId ?? DEFAULT_ANALYZER_ID,
      };
    });

    dispatch(setFields(updatedFields));
  };

  const handleLeftSelection = (_e: any, field: KeyValue) => {
    if (isReadOnlyView) return;
    setLeftSelected((prev) =>
      prev.some((i) => i.id === field.id)
        ? prev.filter((i) => i.id !== field.id)
        : [...prev, field]
    );
  };

  const handleRightSelection = (_e: any, field: KeyValue) => {
    if (isReadOnlyView) return;
    setRightSelected((prev) =>
      prev.some((i) => i.id === field.id)
        ? prev.filter((i) => i.id !== field.id)
        : [...prev, field]
    );
  };

  const moveSelectedToRight = () => {
    if (isReadOnlyView || leftSelected.length === 0) return;
    const updatedRight = [...rightList, ...leftSelected];
    setRightList(updatedRight);
    setLeftList((prev) =>
      prev.filter((i) => !leftSelected.some((s) => s.id === i.id))
    );
    setLeftSelected([]);
    syncToStore(updatedRight);
  };

  const moveSelectedToLeft = () => {
    if (isReadOnlyView || rightSelected.length === 0) return;
    const updatedRight = rightList.filter(
      (i) => !rightSelected.some((s) => s.id === i.id)
    );
    setRightList(updatedRight);
    setLeftList((prev) => [...prev, ...rightSelected]);
    setRightSelected([]);
    syncToStore(updatedRight);
  };

  const moveMultipleToRight = moveSelectedToRight;
  const moveMultipleToLeft = moveSelectedToLeft;

  const handleTransferButtonKeyDown = (e: React.KeyboardEvent) => {
    const buttons = Array.from(
      document.querySelectorAll('.tranfer-action-icon-button')
    ) as HTMLElement[];
    const index = buttons.indexOf(e.currentTarget as HTMLElement);
    if (e.key === 'ArrowDown' && index < buttons.length - 1) {
      e.preventDefault();
      buttons[index + 1].focus();
    }
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      buttons[index - 1].focus();
    }
  };

  return (
    <Box className="metadata-container-create-custom-index">
      <ArrAccordion
        sections={[
          {
            id: 'metaData',
            title: (
              <Typography
                className="metadata-title"
                data-testid={`metadata-fields-metadata-fields`}
              >
                {t('indexManagement.customIndex.stepper.metadataFields')}
              </Typography>
            ),
            content: (
              <Box className="metadate-content">
                <Box className="metadata-heading">
                  <Box className="metadata-content-heading">
                    <Typography
                      className="metadata-content-heading-text"
                      data-testid={`metadata-fields-header-text`}
                    >
                      {t(
                        'indexManagement.customIndex.metadataFields.headerText'
                      )}
                    </Typography>
                  </Box>
                  <Box className="metadata-content-description">
                    <Typography
                      className="metadata-content-description-text"
                      data-testid={`metadata-fields-description`}
                    >
                      {t(
                        'indexManagement.customIndex.metadataFields.description'
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  className={`fields-transfer-wrapper ${
                    isReadOnlyView ? 'readonly-fields-transfer-wrapper' : ''
                  }`}
                >
                  <Box
                    role="region"
                    aria-label="Available Fields"
                    className="transfer-list-box"
                  >
                    <ArrTransferList
                      title={t(
                        'indexManagement.customIndex.metadataFields.availableFields'
                      )}
                      displayedList={filteredLeftList}
                      selectedList={leftSelected}
                      checkboxDataTestId="metadata-fields-left-transfer-list-checkbox"
                      fullWidthSearchField
                      clickOnRow={!isReadOnlyView}
                      onChange={handleLeftSelection}
                      dimSelect={true}
                      showCheckbox={false}
                      hideItemDivider={true}
                      endIcon={(item) =>
                        leftSelected.some((s) => s.id === item.id) ? (
                          <RowChecked />
                        ) : null
                      }
                      endIconPosition="right"
                      showSearchField={true}
                      searchProps={{
                        value: leftSearch,
                        onChange: (e) => setLeftSearch(e.target.value),
                        placeholder: t('verticalNavBar.Search'),
                        type: 'search',
                        inputProps: {'aria-label': 'Search available fields'},
                        dataTestId: 'metadat-fields-transfer-list-left',
                      }}
                    />
                  </Box>
                  <Box className="transfer-action-buttons">
                    <Box className="transfer-buttons-blocks">
                      <ArrIconButton
                        className="tranfer-action-icon-button arrow-double"
                        onClick={moveMultipleToRight}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={leftSelected.length <= 1 || isReadOnlyView}
                        icon={<DoubleArrowBackward />}
                        ariaLabel="Add Multiple"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.addSelectedFields'
                        )}
                        tabIndex={0}
                        testId="metadata-fields-add-multiple-icon-button"
                      />
                      <ArrIconButton
                        className="tranfer-action-icon-button tranfer-action-forward arrow-single"
                        onClick={moveSelectedToRight}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={leftSelected.length !== 1 || isReadOnlyView}
                        icon={<ArrowForward />}
                        ariaLabel="Add Selected"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.addSelectedField'
                        )}
                        tabIndex={0}
                        testId="metadata-fields-add-selected-icon-button"
                      />
                    </Box>
                    <Divider className="move-buton-divider" />
                    <Box className="transfer-buttons-blocks">
                      <ArrIconButton
                        className="tranfer-action-icon-button arrow-single"
                        onClick={moveSelectedToLeft}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={rightSelected.length !== 1 || isReadOnlyView}
                        icon={<ArrowBackward />}
                        ariaLabel="Remove Selected"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.removeSelectedField'
                        )}
                        tabIndex={0}
                        testId="metadata-fields-remove-selected-icon-button"
                      />
                      <ArrIconButton
                        className="tranfer-action-icon-button tranfer-action-forward arrow-double"
                        onClick={moveMultipleToLeft}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={rightSelected.length <= 1 || isReadOnlyView}
                        icon={<DoubleArrowForward />}
                        ariaLabel="Remove Multiple"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.removeSelectedFields'
                        )}
                        tabIndex={0}
                        testId="metadata-fields-remove-multiple-icon-button"
                      />
                    </Box>
                  </Box>
                  <Box
                    role="region"
                    aria-label="Selected Fields"
                    className="transfer-list-box"
                  >
                    <ArrTransferList
                      title={t(
                        'indexManagement.customIndex.metadataFields.selectedFields'
                      )}
                      displayedList={filteredRightList}
                      selectedList={rightSelected}
                      checkboxDataTestId="metadata-fields-right-transfer-list-checkbox"
                      clickOnRow={!isReadOnlyView}
                      onChange={handleRightSelection}
                      fullWidthSearchField
                      showCheckbox={false}
                      dimSelect={true}
                      hideItemDivider={true}
                      endIcon={(item) =>
                        rightSelected.some((s) => s.id === item.id) ? (
                          <RowChecked />
                        ) : null
                      }
                      endIconPosition="right"
                      showSearchField={true}
                      searchProps={{
                        value: rightSearch,
                        onChange: (e) => setRightSearch(e.target.value),
                        placeholder: t('verticalNavBar.Search'),
                        type: 'search',
                        inputProps: {'aria-label': 'Search selected fields'},
                        dataTestId: 'metadat-fields-transfer-list-right',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ),
          },
        ]}
        expandedSection="metaData"
        showExpandIcon={false}
        className="metadata-accordion"
        dataTestId="metadata-fields"
      />
    </Box>
  );
};

export default MetadataFields;
